import { Action } from '@ngrx/store';
import { Paging } from 'src/app/api/types/Pageable';
import {
  BookingTier,
  BulkBookingsUpdateRequest,
  ClientStoreEventBooking,
  ClientStoreEventsBookingsBookingTypeFilter,
  ClientStoreEventsBookingsStatus,
  EntryPass,
  EntryPassResponse,
  EventAttendance,
} from '../store-events-bookings';
import { HideLoader, ShowLoader } from 'src/app/shared/decorators';
import { Event } from '../../event';
import { EntryPassStatus } from './store-event.reducer';

export enum StoreEventsBookingsActionType {
  Initialize = '[storeEventsBooking] Initialize',
  LoadStoreEventsBookings = '[storeEventsBooking] LoadStoreEventsBookings',
  RefreshStoreEventsBookings = '[storeEventsBooking] RefreshStoreEventsBookings',
  UpdateBookingData = '[storeEventsBooking] UpdateBookingData',
  LoadStoreEventsBookingsSuccess = '[storeEventsBooking] LoadStoreEventsBookingsSuccess',
  LoadStoreEventsBookingsFail = '[storeEventsBooking] LoadStoreEventsBookingsFail',
  UpdateBookingStatus = '[storeEventsBooking] UpdateBookingStatus',
  UpdateBookingStatusSuccess = '[storeEventsBooking] UpdateBookingStatusSuccess',
  UpdateBookingStatusFail = '[storeEventsBooking] UpdateBookingStatusFail',
  UpdateBulkBookingStatus = '[storeEventsBooking] UpdateBulkBookingStatus',
  UpdateBulkBookingStatusSuccess = '[storeEventsBooking] UpdateBulkBookingStatusSuccess',
  UpdateBulkBookingStatusFail = '[storeEventsBooking] UpdateBulkBookingStatusFail',
  LoadEventBooking = '[storeEventsBooking] LoadEventBooking',
  LoadStoreEventsBookingsByOwnerId = '[storeEventsBooking] LoadStoreEventsBookingsByOwnerId',
  LoadStoreEventBookingSuccess = '[storeEventsBooking] LoadStoreEventBookingSuccess',
  LoadStoreEventBookingFail = '[storeEventsBooking] LoadStoreEventBookingFail',
  AddSelectedBooking = '[storeEventsBooking] AddSelectedBooking',
  RemoveSelectedBooking = '[storeEventsBooking] RemoveSelectedBooking',
  ClearSelectedBookings = '[storeEventsBooking] ClearSelectedBookings',
  SetSelectedBookings = '[storeEventsBooking] SetSelectedBookings',
  ResetSelectedBooking = '[storeEventsBooking] ResetSelectedBooking',
  LoadEventAttendanceReset = '[storeEventsBooking] LoadEventAttendanceReset',
  LoadEventAttendance = '[storeEventsBooking] LoadEventAttendance',
  LoadEventAttendanceSuccess = '[storeEventsBooking] LoadEventAttendanceSuccess',
  LoadEventAttendanceFail = '[storeEventsBooking] LoadEventAttendanceFail',
  CheckEntryPass = '[storeEventsBooking] CheckEntryPass',
  CheckEntryPassSuccess = '[storeEventsBooking] CheckEntryPassSuccess',
  CheckEntryPassFailed = '[storeEventsBooking] CheckEntryPassFailed',
  InitializeCheckEntryPass = '[storeEventsBooking] InitializeCheckEntryPass',
  LoadBookingTiersReset = '[storeEventsBooking] LoadBookingTiersReset',
  LoadBookingTiers = '[storeEventsBooking] LoadBookingTiers',
  LoadBookingTiersSuccess = '[storeEventsBooking] LoadBookingTiersSuccess',
  LoadBookingTiersFail = '[storeEventsBooking] LoadBookingTiersFail',
  UpdateBookingNotes = '[storeEventsBooking] UpdateBookingNotes',
  UpdateBookingNotesSuccess = '[storeEventsBooking] UpdateBookingNotesSuccess',
  UpdateBookingNotesFail = '[storeEventsBooking] UpdateBookingNotesFail',
  DownloadEventCustomerXls = '[storeEventsBooking] DownloadEventCustomerXls',
  DownloadEventCustomerXlsSuccess = '[storeEventsBooking] DownloadEventCustomerXlsSuccess',
  DownloadEventCustomerXlsFailed = '[storeEventsBooking] DownloadEventCustomerXlsFailed',
}

export class Initialize implements Action {
  readonly type = StoreEventsBookingsActionType.Initialize;

  constructor() {}
}

export class ResetSelectedBooking implements Action {
  readonly type = StoreEventsBookingsActionType.ResetSelectedBooking;

  constructor() {}
}
export interface LoadStoreEventsBookingsPayload {
  bookingStatus?: ClientStoreEventsBookingsStatus | null;
  eventId?: number | null;
  eventOccurrenceId?: number | null;
  occurrenceStartDateTimeUTC?: string | null;
  bookingType?: ClientStoreEventsBookingsBookingTypeFilter | null;
  /**
   * Guest List filter — `true` for Only Guest List, `false` for Excluding
   * Guest List, `null` / omitted for No filter.
   */
  guestList?: boolean | null;
  ownerId?: number | null;
  ownerName?: string | null;
  bookingId?: number | null;
  bookingNumber?: string | null;
  paging?: Paging | null;
  event?: Event;
  sortBy?: string;
  sortDirection?: 'desc';
}

export class LoadStoreEventsBookings implements Action {
  readonly type = StoreEventsBookingsActionType.LoadStoreEventsBookings;
  constructor(public readonly payload: LoadStoreEventsBookingsPayload) {
    this.payload.paging = this.payload.paging ?? { page: 0, size: 10 };
  }
}

/**
 * Reload the bookings list while preserving every active filter. Carries no
 * payload: the effect rebuilds the request from the persisted list state via
 * the same source of truth used after a status change, so callers never have
 * to re-specify (and risk dropping) event/sub-event/occurrence/type/guest-list
 * filters. See `onRefreshStoreEventsBookings`.
 */
export class RefreshStoreEventsBookings implements Action {
  readonly type = StoreEventsBookingsActionType.RefreshStoreEventsBookings;

  constructor() {}
}

/**
 * Patch a single booking's fresh data into state wherever it is held — the
 * bookings list row and (when it is the open one) the selected-booking detail.
 * Carries the already-fetched booking so no re-query is needed: this avoids the
 * flicker (no LOADING state) and the projection lag of a full list refresh.
 */
export class UpdateBookingData implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBookingData;

  constructor(public readonly booking: ClientStoreEventBooking) {}
}

export class LoadStoreEventsBookingsByOwnerId implements Action {
  readonly type = StoreEventsBookingsActionType.LoadStoreEventsBookings;

  constructor(
    public readonly payload: {
      ownerId?: number | null;
      paging?: Paging | null;
      event?: Event;
      sortBy?: string;
      sortDirection?: string;
      eventOccurrenceId?: number | null;
      occurrenceStartDateTimeUTC?: string | null;
    },
  ) {
    this.payload.paging = this.payload.paging ?? { page: 0, size: 10 };
  }
}

export class LoadStoreEventsBookingsSuccess implements Action {
  readonly type = StoreEventsBookingsActionType.LoadStoreEventsBookingsSuccess;

  constructor(
    public readonly bookings: ClientStoreEventBooking[],
    public readonly totalElements: number,
    public readonly pageNumber: number,
    public readonly pageSize: number,
    public readonly event?: Event,
  ) {}
}

export class LoadStoreEventsBookingsFail implements Action {
  readonly type = StoreEventsBookingsActionType.LoadStoreEventsBookingsFail;

  constructor(public readonly event?: Event) {}
}

export class UpdateBookingStatus implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBookingStatus;

  constructor(
    public readonly bookingId: number,
    public readonly status: ClientStoreEventsBookingsStatus,
    public readonly requireConfirmation?: boolean,
  ) {}
}

export class UpdateBookingStatusSuccess implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBookingStatusSuccess;

  constructor(public readonly id: number) {}
}

export class UpdateBookingStatusFail implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBookingStatusFail;
}

export class UpdateBulkBookingStatus implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBulkBookingStatus;

  constructor(
    public readonly bulkBookingsUpdateRequest: BulkBookingsUpdateRequest,
  ) {}
}

export class UpdateBulkBookingStatusSuccess implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBulkBookingStatusSuccess;

  constructor() {}
}

export class UpdateBulkBookingStatusFail implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBulkBookingStatusFail;
}

export class LoadEventBooking implements Action {
  readonly type = StoreEventsBookingsActionType.LoadEventBooking;

  constructor(
    public readonly id: number,
    public readonly storeId?: number,
    public readonly stationId?: number,
  ) {}
}
@HideLoader(StoreEventsBookingsActionType.LoadEventBooking)
export class LoadStoreEventBookingSuccess implements Action {
  readonly type = StoreEventsBookingsActionType.LoadStoreEventBookingSuccess;

  constructor(public readonly booking: ClientStoreEventBooking) {}
}
@HideLoader(StoreEventsBookingsActionType.LoadEventBooking)
export class LoadStoreEventBookingFail implements Action {
  readonly type = StoreEventsBookingsActionType.LoadStoreEventBookingFail;

  constructor() {}
}

export class AddSelectedBooking implements Action {
  readonly type = StoreEventsBookingsActionType.AddSelectedBooking;

  constructor(public readonly bookingId: string) {}
}

export class RemoveSelectedBooking implements Action {
  readonly type = StoreEventsBookingsActionType.RemoveSelectedBooking;

  constructor(public readonly bookingId: string) {}
}

export class ClearSelectedBookings implements Action {
  readonly type = StoreEventsBookingsActionType.ClearSelectedBookings;

  constructor() {}
}

export class SetSelectedBookings implements Action {
  readonly type = StoreEventsBookingsActionType.SetSelectedBookings;

  constructor() {}
}

export class LoadEventAttendanceReset implements Action {
  readonly type = StoreEventsBookingsActionType.LoadEventAttendanceReset;

  constructor() {}
}
@ShowLoader()
export class LoadEventAttendance implements Action {
  readonly type = StoreEventsBookingsActionType.LoadEventAttendance;
  constructor(
    public readonly id: number,
    public readonly occurrenceStartDateTimeUTC?: string | null,
  ) {}
}
@HideLoader(StoreEventsBookingsActionType.LoadEventAttendance)
export class LoadEventAttendanceSuccess implements Action {
  readonly type = StoreEventsBookingsActionType.LoadEventAttendanceSuccess;

  constructor(public readonly attendance: EventAttendance) {}
}
@HideLoader(StoreEventsBookingsActionType.LoadEventAttendance)
export class LoadEventAttendanceFail implements Action {
  readonly type = StoreEventsBookingsActionType.LoadEventAttendanceFail;

  constructor() {}
}

export class InitializeCheckEntryPass implements Action {
  readonly type = StoreEventsBookingsActionType.InitializeCheckEntryPass;

  constructor() {}
}

export class CheckEntryPass implements Action {
  readonly type = StoreEventsBookingsActionType.CheckEntryPass;

  constructor(
    public readonly storeId: string,

    public readonly eventId: string | null,

    public readonly entryPassId: string,
    public readonly occurrenceStartDateTimeUTC?: string | null,
  ) {}
}

export class CheckEntryPassSuccess implements Action {
  readonly type = StoreEventsBookingsActionType.CheckEntryPassSuccess;

  constructor(public readonly entryPass: EntryPass) {}
}

export class CheckEntryPassFailed implements Action {
  readonly type = StoreEventsBookingsActionType.CheckEntryPassFailed;

  constructor(
    public readonly error: EntryPassResponse,
    public readonly entryPassStatus: EntryPassStatus,
  ) {}
}

export class LoadBookingTiersReset implements Action {
  readonly type = StoreEventsBookingsActionType.LoadBookingTiersReset;

  constructor() {}
}
export class LoadBookingTiers implements Action {
  readonly type = StoreEventsBookingsActionType.LoadBookingTiers;

  constructor(public readonly id: number) {}
}

export class LoadBookingTiersSuccess implements Action {
  readonly type = StoreEventsBookingsActionType.LoadBookingTiersSuccess;

  constructor(public readonly bookingTiers: BookingTier[]) {}
}

export class LoadBookingTiersFail implements Action {
  readonly type = StoreEventsBookingsActionType.LoadBookingTiersFail;

  constructor() {}
}

export class UpdateBookingNotes implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBookingNotes;
  constructor(
    public readonly storeId: number,
    public readonly bookingId: number,
    public readonly notes: string,
  ) {}
}

export class UpdateBookingNotesSuccess implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBookingNotesSuccess;
  constructor(public readonly booking: any) {}
}

export class UpdateBookingNotesFail implements Action {
  readonly type = StoreEventsBookingsActionType.UpdateBookingNotesFail;
  constructor() {}
}

export class DownloadEventCustomerXls implements Action {
  readonly type = StoreEventsBookingsActionType.DownloadEventCustomerXls;
  constructor(
    public readonly storeId: number,
    public readonly eventId: number,
  ) {}
}

export class DownloadEventCustomerXlsSuccess implements Action {
  readonly type = StoreEventsBookingsActionType.DownloadEventCustomerXlsSuccess;
  constructor(
    public readonly blob: Blob,
    public readonly filename: string,
  ) {}
}

export class DownloadEventCustomerXlsFailed implements Action {
  readonly type = StoreEventsBookingsActionType.DownloadEventCustomerXlsFailed;
}

export type StoreEventsBookingsAction =
  | Initialize
  | ResetSelectedBooking
  | LoadStoreEventsBookings
  | RefreshStoreEventsBookings
  | UpdateBookingData
  | LoadStoreEventsBookingsSuccess
  | LoadStoreEventsBookingsFail
  | UpdateBookingStatus
  | UpdateBookingStatusSuccess
  | UpdateBookingStatusFail
  | UpdateBulkBookingStatus
  | UpdateBulkBookingStatusSuccess
  | UpdateBulkBookingStatusFail
  | LoadEventBooking
  | LoadStoreEventsBookingsByOwnerId
  | LoadStoreEventBookingSuccess
  | LoadStoreEventBookingFail
  | AddSelectedBooking
  | RemoveSelectedBooking
  | ClearSelectedBookings
  | SetSelectedBookings
  | LoadEventAttendanceReset
  | LoadEventAttendance
  | LoadEventAttendanceSuccess
  | LoadEventAttendanceFail
  | CheckEntryPass
  | CheckEntryPassSuccess
  | CheckEntryPassFailed
  | InitializeCheckEntryPass
  | LoadBookingTiersReset
  | LoadBookingTiers
  | LoadBookingTiersSuccess
  | LoadBookingTiersFail
  | UpdateBookingNotes
  | UpdateBookingNotesSuccess
  | UpdateBookingNotesFail
  | DownloadEventCustomerXls
  | DownloadEventCustomerXlsSuccess
  | DownloadEventCustomerXlsFailed;
